.root {
  --ds-button-color: inherit;
  --ds-button-border-color: inherit;
  --ds-button-background-color: inherit;
  --ds-button-icon-size: inherit;
  --ds-button-gap: inherit;
  --ds-button-block-size: inherit;
  --ds-button-padding-inline: inherit;
  --ds-button-border-radius: inherit;
  --ds-button-border-inline-start-width: inherit;
  --ds-button-border-inline-end-width: inherit;
  --ds-button-border-end-radius: inherit;
  --ds-button-border-start-radius: inherit;
  --ds-button-icon-color: inherit;
  --ds-button-font-size: inherit;
  --ds-button-justify-content: inherit;
  --ds-button-inner-justify-content: inherit;

  display: inline-grid;
  align-items: center;
  justify-content: var(--ds-button-justify-content, center);
  user-select: none;
  white-space: nowrap;
  position: relative;
  border-style: solid;
  line-height: var(--line-height__none);
  text-box-edge: cap alphabetic;
  text-box-trim: trim-both;
  /** TODO remove !important when we get rid of primer's BaseStyles */
  text-decoration: none !important;

  border-block-width: 1px;
  border-inline-start-width: var(--ds-button-border-inline-start-width, 1px);
  border-inline-end-width: var(--ds-button-border-inline-end-width, 1px);
  border-color: var(--ds-button-border-color, var(--ds-button-border-color-default));
  background-color: var(--ds-button-background-color, var(--ds-button-background-color-default));
  color: var(--ds-button-color, var(--ds-button-color-default));
  min-block-size: var(--ds-button-block-size, var(--ds-button-block-size-default));
  min-inline-size: var(--ds-button-block-size, var(--ds-button-block-size-default));
  border-end-end-radius: var(--ds-button-border-end-radius, var(--ds-button-border-radius-default));
  border-start-end-radius: var(--ds-button-border-end-radius, var(--ds-button-border-radius-default));
  border-start-start-radius: var(--ds-button-border-start-radius, var(--ds-button-border-radius-default));
  border-end-start-radius: var(--ds-button-border-start-radius, var(--ds-button-border-radius-default));
  font-size: var(--ds-button-font-size, var(--ds-button-font-size-default));
  font-weight: var(--font-weight__medium);
}

/* Primer's BaseStyles colours every non-Primer `<a>` with the accent colour at the same specificity
 * as `.root`, and its stylesheet loads later, so without this a LinkButton takes the accent colour
 * instead of its own (ink on ink for a contained primary in the unified shell). The element selector
 * outranks it. TODO remove with primer's BaseStyles, like the text-decoration !important above. */
a.root {
  color: var(--ds-button-color, var(--ds-button-color-default));
}

.small {
  --ds-button-gap-default: var(--space__sm);
  --ds-button-block-size-default: 1.5rem;
  --ds-button-border-radius-default: var(--border-radius__md);
  --ds-button-padding-inline-default: var(--space__md);
  --ds-button-icon-size-default: 0.75rem;
  --ds-button-font-size-default: var(--font-size__sm);
  padding-block: var(--space__sm);
}

.medium {
  --ds-button-gap-default: var(--space__sm);
  --ds-button-block-size-default: 2rem;
  --ds-button-border-radius-default: var(--border-radius__md);
  --ds-button-padding-inline-default: var(--space__md);
  --ds-button-icon-size-default: 1rem;
  --ds-button-font-size-default: var(--font-size__md);
}

.large {
  --ds-button-gap-default: var(--space__md);
  --ds-button-block-size-default: 2.5rem;
  --ds-button-border-radius-default: var(--border-radius__md);
  --ds-button-padding-inline-default: var(--space__lg);
  --ds-button-icon-size-default: 1rem;
  --ds-button-font-size-default: var(--font-size__md);
}

.inner {
  display: inline-grid;
  align-items: center;
  justify-content: var(--ds-button-inner-justify-content, center);
  overflow: hidden;
  block-size: 100%;
  inline-size: 100%;
  gap: var(--ds-button-gap, var(--ds-button-gap-default));
  grid-auto-flow: column;
}

.root:has(.content:not(:empty, [data-content-visually-hidden])) {
  padding-inline: var(--ds-button-padding-inline, var(--ds-button-padding-inline-default));
}

/* to remove the gap between the content and icons  */
.content:is(:empty, [data-content-visually-hidden]) {
  display: none;
}

.svg {
  inline-size: var(--ds-button-icon-size, var(--ds-button-icon-size-default));
  block-size: var(--ds-button-icon-size, var(--ds-button-icon-size-default));
}

.icon {
  /* https://github.com/adobe/react-spectrum/issues/5962#issuecomment-1970246060 */
  pointer-events: none;
  color: var(--ds-button-icon-color, var(--ds-button-icon-color-default));
}

.root[data-pending] :is(.icon, .content) {
  opacity: 0;
}

.root:not([data-pending]) .spinner {
  display: none;
}

.root[data-pending] .spinner {
  display: flex;
}

.spinner {
  position: absolute;
  inset: 0;
  align-items: center;
  justify-content: center;
}

.root[data-disabled] .spinner {
  filter: grayscale(100%) opacity(50%);
}

.root:is(a, button):not([data-disabled]) {
  cursor: pointer;
}

.root:is(a, button)[data-disabled] {
  cursor: not-allowed;
}

.root[data-disabled] {
  cursor: not-allowed;
}

.root[data-focus-visible] {
  outline: var(--focus-ring__width) solid var(--focus-ring__color);
  /* also overrides the browser's default 1px offset for links */
  outline-offset: var(--focus-ring__offset);
}

.root:not([data-focus-visible]) {
  outline: 0;
}

.contained {
  --ds-button-icon-color-default: var(--color__white);

  &.danger {
    &:not([data-disabled]) {
      &:not([data-hovered], [data-focus-visible]) {
        --ds-button-background-color-default: var(--color__red-600);
        --ds-button-border-color-default: var(--color__red-600);
        --ds-button-color-default: var(--color__red-50);
      }

      &:is([data-hovered], [data-focus-visible]) {
        --ds-button-background-color-default: var(--color__red-700);
        --ds-button-border-color-default: var(--color__red-700);
        --ds-button-color-default: var(--color__red-50);
      }
    }

    &[data-disabled] {
      --ds-button-background-color-default: var(--color__red-300);
      --ds-button-border-color-default: var(--color__red-300);
      --ds-button-color-default: var(--color__white);
    }
  }

  &.primary {
    &:not([data-disabled]) {
      &:not([data-hovered], [data-focus-visible]) {
        --ds-button-background-color-default: var(--color__gray-900);
        --ds-button-border-color-default: var(--color__gray-900);
        --ds-button-color-default: var(--color__gray-50);
      }

      &:is([data-hovered], [data-focus-visible]) {
        --ds-button-background-color-default: var(--color__black);
        --ds-button-border-color-default: var(--color__black);
        --ds-button-color-default: var(--color__gray-50);
      }
    }

    &[data-disabled] {
      --ds-button-background-color-default: var(--color__gray-500);
      --ds-button-border-color-default: var(--color__gray-500);
      --ds-button-color-default: var(--color__white);
    }
  }
}

.outlined {
  &.danger {
    &:not([data-disabled]) {
      --ds-button-color-default: var(--color__red-600);
      --ds-button-icon-color-default: var(--color__red-600);

      &[data-focus-visible] {
        --ds-button-border-color-default: var(--color__red-400);
      }

      &:not([data-focus-visible]) {
        --ds-button-border-color-default: var(--color__red-300);
      }

      &:not([data-focus-visible])[data-hovered] {
        --ds-button-background-color-default: var(--color__red-50);
      }

      &:not([data-hovered]),
      &[data-focus-visible] {
        --ds-button-background-color-default: var(--color__white);
      }
    }

    &[data-disabled] {
      --ds-button-background-color-default: var(--color__red-300);
      --ds-button-border-color-default: var(--color__red-300);
      --ds-button-color-default: var(--color__white);
      --ds-button-icon-color-default: currentColor;
    }
  }

  &.primary {
    &:not([data-disabled]) {
      --ds-button-color-default: var(--color__gray-800);
      --ds-button-icon-color-default: var(--color__gray-500);

      &[data-focus-visible] {
        --ds-button-border-color-default: var(--color__gray-400);
      }

      &:not([data-focus-visible]) {
        --ds-button-border-color-default: var(--color__gray-300);
      }

      &:not([data-focus-visible])[data-hovered] {
        --ds-button-background-color-default: var(--color__gray-50);
      }

      &:not([data-hovered]),
      &[data-focus-visible] {
        --ds-button-background-color-default: var(--color__white);
      }
    }

    &[data-disabled] {
      --ds-button-border-color-default: var(--color__gray-100);
      --ds-button-background-color-default: var(--color__gray-100);
      --ds-button-color-default: var(--color__gray-400);
      --ds-button-icon-color-default: currentColor;
    }
  }
}

.text {
  &.danger {
    &:not([data-disabled]) {
      --ds-button-color-default: var(--color__red-600);
      --ds-button-icon-color-default: var(--color__red-600);

      &:not([data-hovered]),
      &[data-focus-visible] {
        --ds-button-border-color-default: transparent;
        --ds-button-background-color-default: transparent;
      }

      &:not([data-focus-visible])[data-hovered] {
        --ds-button-border-color-default: var(--color__red-50);
        --ds-button-background-color-default: var(--color__red-50);
      }
    }

    &[data-disabled] {
      --ds-button-background-color-default: var(--color__red-300);
      --ds-button-border-color-default: var(--color__red-300);
      --ds-button-color-default: var(--color__white);
      --ds-button-icon-color-default: currentColor;
    }
  }

  &.primary {
    &:not([data-disabled]) {
      --ds-button-color-default: var(--color__gray-800);
      --ds-button-icon-color-default: var(--color__gray-500);

      &:not([data-hovered]),
      &[data-focus-visible] {
        --ds-button-border-color-default: transparent;
        --ds-button-background-color-default: transparent;
      }

      &:not([data-focus-visible])[data-hovered] {
        --ds-button-border-color-default: var(--color__gray-100);
        --ds-button-background-color-default: var(--color__gray-100);
      }
    }

    &[data-disabled] {
      --ds-button-border-color-default: var(--color__gray-100);
      --ds-button-background-color-default: var(--color__gray-100);
      --ds-button-color-default: var(--color__gray-400);
      --ds-button-icon-color-default: currentColor;
    }
  }
}

.info {
  border-radius: var(--border-radius__md);
  position: absolute;
  inset: 0;
}
